import {
	type AgentSession,
	type Artifact,
	type ArtifactContent,
	type AuthenticationProfile,
	AuthenticationProfilePageSchema,
	AuthenticationProfileSchema,
	type AuthSession,
	AuthSessionSchema,
	type ChatMessage,
	ConnectionTestResultSchema,
	Headers as ContractHeaders,
	type ConversationFrame,
	type ConversationFramesSnapshot,
	type CreateAgentSessionRequest,
	type CreateArtifactRequest,
	type CreateProjectRequest,
	DesignApprovalPayloadSchema,
	type EnsureGitRepositoryRequest,
	type EnsureGitRepositoryResponse,
	type GitRepository,
	type LlmUsageSummary,
	type LoginRequest,
	type Page,
	type ProgressEvent,
	ProgressEventSchema,
	type Project,
	ProviderPageSchema,
	type SendChatMessageRequest,
	type SessionBootstrap,
	SessionBootstrapSchema,
	type SignupRequest,
	type StartExecutionRequest,
	StartExecutionRequestSchema,
	SystemAuthAssignmentPageSchema,
	SystemAuthPoolPageSchema,
	type WaitHandle,
	type WorkflowExecution,
	type WorkOrder,
	type WorkOrderEvidence,
	type WorkOrderProgress,
	type WorkOrderQuestion,
	type WorkOrderRecord,
	type WorkOrderSession,
	type WorkOrderSessionRole,
	type WorkOrderStatus,
	type WorkspaceTypeList,
} from "@agentry/zerovia-contracts";
import manifest from "@agentry/zerovia-contracts/manifest.json" with { type: "json" };
import type {
	ApprovalDecision,
	ApprovalRequest,
	AuthRouteView,
	Budget,
	ByoPolicy,
	CatalogPublication,
	ContactMessage,
	DownloadTicket,
	Integration,
	Notice,
	NotificationItem,
	OwnershipTransferRecord,
	PersonalCredential,
	Plan,
	PoolAssignment,
	ProductSettings,
	ProviderUsageRule,
	RoleDefinition,
	StorageObject,
	TenantCatalogEntitlement,
	TenantInvite,
	TenantMember,
	TenantSettings,
	TenantSummary,
	UsageSummary,
	UserAttributeMap,
	WorkspaceEntry,
} from "../agentry-types.js";
import { ENDPOINTS } from "../endpoints.js";
import { ApiError, ContractMismatchError, TransportError } from "../errors.js";
import { MockZeroviaClient } from "../mock/client.js";
import type {
	ApiMode,
	AuditEventView,
	AuthProfileView,
	Backing,
	Catalog,
	CommandOptions,
	CostEventView,
	DashboardSummary,
	ExecutionTimeline,
	ListQuery,
	ProgressSubscription,
	ProjectListItemView,
	ProjectRepositoryConnection,
	ProviderView,
	Scope,
	SessionDetail,
	SubscribeOptions,
	UserInputSubmission,
	WorkflowCommand,
	WorkOrderFrameView,
	WorkOrderProgressView,
	WorkOrderProjectSettings,
	WorkOrderStreamOptions,
	ZeroviaClient,
} from "../types.js";

const SESSION_KEY = "agentry.real.auth.v1";
const ACCESS_REFRESH_MARGIN_MS = 5 * 60_000;

export type HttpClientOptions = {
	apiUrl: string;
	authUrl: string;
	fetchImpl?: typeof fetch;
	/**
	 * Zerovia未実装領域（Tenant Admin / System Adminの一部）のFallback。
	 * `backingOf()`は必ず MOCK_ONLY を返し、接続済みと誤表示しない。
	 */
	localFallback?: ZeroviaClient;
};

/**
 * Real Zerovia Client。
 *
 * 失敗をMockの成功で覆い隠さない。通信失敗は TransportError、
 * Contract不一致は ContractMismatchError として必ず表面化させる。
 */
export class HttpZeroviaClient implements ZeroviaClient {
	readonly mode: ApiMode = "real";
	private readonly fetchImpl: typeof fetch;
	private readonly fallback: ZeroviaClient;
	private auth: AuthSession | null;
	private refreshPromise: Promise<AuthSession> | null = null;
	private readonly designSessions = new Map<string, string>();
	private readonly localFrames = new Map<string, WorkOrderFrameView[]>();

	constructor(private readonly options: HttpClientOptions) {
		this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
		this.fallback = options.localFallback ?? new MockZeroviaClient({ latencyMs: 0 });
		this.auth = readStoredAuth();
		this.adoptIntoFallback(this.auth);
	}

	backingOf(operation: keyof ZeroviaClient): Backing {
		return ZEROVIA_OPERATIONS.has(operation) ? "ZEROVIA" : "MOCK_ONLY";
	}

	async verifyContract() {
		const endpoint = this.apiEndpoint(ENDPOINTS.manifest());
		const body = await this.raw<{ contractVersion?: string; contractHash?: string }>(endpoint);
		if (
			body.contractVersion !== manifest.contractVersion ||
			body.contractHash !== manifest.contractHash
		) {
			throw new ContractMismatchError(
				{ contractVersion: manifest.contractVersion, contractHash: manifest.contractHash },
				body,
				endpoint,
			);
		}
		return {
			contractVersion: manifest.contractVersion,
			contractHash: manifest.contractHash,
			verified: true,
		};
	}

	// -- transport -----------------------------------------------------------

	private apiEndpoint(path: string): string {
		return `${this.options.apiUrl}${path}`;
	}

	private authEndpoint(path: string): string {
		return `${this.options.authUrl}${path}`;
	}

	private async raw<T>(
		url: string,
		init: RequestInit = {},
		options: { skipAuth?: boolean; retryAuth?: boolean } = {},
	): Promise<T> {
		const skipAuth = options.skipAuth ?? false;
		if (!skipAuth && this.auth && accessNeedsRefresh(this.auth)) {
			await this.refreshAuth();
		}
		let response: Response;
		try {
			response = await this.fetchImpl(url, {
				...init,
				headers: {
					"content-type": "application/json",
					[ContractHeaders.CONTRACT_VERSION]: manifest.contractVersion,
					...(!skipAuth && this.auth ? { authorization: `Bearer ${this.auth.accessToken}` } : {}),
					...(init.headers as Record<string, string> | undefined),
				},
			});
		} catch (cause) {
			throw new TransportError(`Zerovia APIへ接続できませんでした: ${url}`, url, cause);
		}
		if (response.status === 401 && !skipAuth && this.auth && options.retryAuth !== false) {
			await this.refreshAuth();
			return this.raw<T>(url, init, { retryAuth: false });
		}

		if (response.status === 204) return undefined as T;
		const text = await response.text();
		const payload = text ? safeJson(text) : null;

		if (!response.ok) {
			const error = (
				payload as { error?: { code: string; message: string; reason?: string; details?: unknown } }
			)?.error;
			throw new ApiError({
				code: error?.code ?? "INTERNAL",
				message: error?.message ?? `HTTP ${response.status}`,
				reason: error?.reason,
				status: response.status,
				details: error?.details,
			});
		}
		return payload as T;
	}

	private get<T>(url: string): Promise<T> {
		return this.raw<T>(url);
	}

	private post<T>(url: string, body: unknown, headers: Record<string, string> = {}): Promise<T> {
		return this.raw<T>(url, { method: "POST", body: JSON.stringify(body ?? {}), headers });
	}

	private put<T>(url: string, body: unknown): Promise<T> {
		return this.raw<T>(url, { method: "PUT", body: JSON.stringify(body ?? {}) });
	}

	private delete<T>(url: string): Promise<T> {
		return this.raw<T>(url, { method: "DELETE" });
	}

	private query(query?: ListQuery & Record<string, unknown>): string {
		if (!query) return "";
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(query)) {
			if (value !== undefined && value !== null) params.set(key, String(value));
		}
		const text = params.toString();
		return text ? `?${text}` : "";
	}

	// -- auth ----------------------------------------------------------------

	async login(input: LoginRequest): Promise<AuthSession> {
		const payload = await this.raw<unknown>(
			this.authEndpoint(ENDPOINTS.login()),
			{ method: "POST", body: JSON.stringify(input) },
			{ skipAuth: true },
		);
		return this.storeAuth(AuthSessionSchema.parse(payload));
	}

	async signup(input: SignupRequest): Promise<SessionBootstrap> {
		const payload = await this.raw<unknown>(
			this.authEndpoint(ENDPOINTS.signup()),
			{ method: "POST", body: JSON.stringify(input) },
			{ skipAuth: true },
		);
		return this.storeAuth(SessionBootstrapSchema.parse(payload));
	}

	async me(): Promise<AuthSession | null> {
		if (!this.auth) return null;
		// Tokenの生存確認。失効していれば呼び出し側がLoginへ戻す。
		await this.get(this.authEndpoint(ENDPOINTS.me()));
		return this.auth;
	}

	async logout(): Promise<void> {
		this.clearAuth();
	}

	async getUserAttributes(userId: string): Promise<UserAttributeMap> {
		try {
			return parseUserAttributeMap(
				userId,
				await this.get<unknown>(this.authEndpoint(ENDPOINTS.userAttributes(userId))),
			);
		} catch (error) {
			if (error instanceof ApiError && (error.status === 404 || error.status === 501)) {
				return { userId, items: [] };
			}
			throw error;
		}
	}

	async upsertUserAttributes(
		userId: string,
		attributes: Record<string, string>,
	): Promise<UserAttributeMap> {
		return parseUserAttributeMap(
			userId,
			await this.put<unknown>(this.authEndpoint(ENDPOINTS.userAttributes(userId)), {
				attributes,
			}),
		);
	}

	private clearAuth(): void {
		this.auth = null;
		this.adoptIntoFallback(null);
		try {
			localStorage?.removeItem(SESSION_KEY);
		} catch {
			/* storage unavailable */
		}
	}

	private refreshAuth(): Promise<AuthSession> {
		if (this.refreshPromise) return this.refreshPromise;
		const current = this.auth;
		if (!current || refreshExpired(current)) {
			this.clearAuth();
			return Promise.reject(
				new ApiError({
					code: "UNAUTHORIZED",
					message: "セッションの更新期限が切れました。",
					status: 401,
				}),
			);
		}
		this.refreshPromise = this.requestRefresh(current.refreshToken).finally(() => {
			this.refreshPromise = null;
		});
		return this.refreshPromise;
	}

	private async requestRefresh(refreshToken: string): Promise<AuthSession> {
		const url = this.authEndpoint(ENDPOINTS.refresh());
		let response: Response;
		try {
			response = await this.fetchImpl(url, {
				method: "POST",
				body: JSON.stringify({ refreshToken }),
				headers: {
					"content-type": "application/json",
					[ContractHeaders.CONTRACT_VERSION]: manifest.contractVersion,
				},
			});
		} catch (cause) {
			throw new TransportError(`Zerovia Authへ接続できませんでした: ${url}`, url, cause);
		}
		const text = await response.text();
		const payload = text ? safeJson(text) : null;
		if (!response.ok) {
			if (response.status === 400 || response.status === 401) this.clearAuth();
			const error = (
				payload as { error?: { code: string; message: string; reason?: string; details?: unknown } }
			)?.error;
			throw new ApiError({
				code: error?.code ?? "UNAUTHORIZED",
				message: error?.message ?? `HTTP ${response.status}`,
				reason: error?.reason,
				status: response.status,
				details: error?.details,
			});
		}
		return this.storeAuth(AuthSessionSchema.parse(payload));
	}

	requestPasswordReset(email: string) {
		// Zerovia側にEndpointがないためMock委譲。UIはMOCK_ONLYバッジで表示する。
		return this.fallback.requestPasswordReset(email);
	}

	acceptInvite(token: string, input: { name: string; password: string }) {
		return this.fallback.acceptInvite(token, input);
	}

	private storeAuth<T extends AuthSession>(session: T): T {
		this.auth = session;
		// MOCK_ONLY操作を担うfallbackにも同じSessionを渡す。
		// 渡さないと、Real modeで管理画面がTOKEN_INVALIDで落ちる。
		this.adoptIntoFallback(session);
		try {
			localStorage?.setItem(SESSION_KEY, JSON.stringify(session));
		} catch {
			/* storage unavailable */
		}
		return session;
	}

	private adoptIntoFallback(session: AuthSession | null): void {
		const adopter = this.fallback as { adoptSession?: (value: AuthSession | null) => void };
		adopter.adoptSession?.(session);
	}

	// -- zerovia-backed -------------------------------------------------------

	getCatalog(scope: Scope): Promise<Catalog> {
		return this.get<Catalog>(this.apiEndpoint(ENDPOINTS.catalog(scope.tenantId, scope.projectId)));
	}

	getDashboard(scope: Scope): Promise<DashboardSummary> {
		return this.get<DashboardSummary>(
			this.apiEndpoint(ENDPOINTS.dashboard(scope.tenantId, scope.projectId)),
		);
	}

	listProviders(scope: Scope): Promise<Page<ProviderView>> {
		return this.get<Page<ProviderView>>(
			this.apiEndpoint(ENDPOINTS.providers(scope.tenantId, scope.projectId)),
		);
	}

	listAuthProfiles(scope: Scope): Promise<Page<AuthProfileView>> {
		return this.get<Page<AuthProfileView>>(
			this.apiEndpoint(ENDPOINTS.authenticationProfiles(scope.tenantId, scope.projectId)),
		);
	}

	listSessions(scope: Scope, query?: ListQuery): Promise<Page<AgentSession>> {
		return this.get<Page<AgentSession>>(
			`${this.apiEndpoint(ENDPOINTS.sessions(scope.tenantId, scope.projectId))}${this.query(query)}`,
		);
	}

	createSession(scope: Scope, input: CreateAgentSessionRequest): Promise<AgentSession> {
		return this.post<AgentSession>(
			this.apiEndpoint(ENDPOINTS.sessions(scope.tenantId, scope.projectId)),
			input,
		);
	}

	async getSession(scope: Scope, sessionId: string): Promise<SessionDetail> {
		// Zeroviaの公開APIはBlockごとに正規化されており、Session単体・Chat・
		// Workflow・Artifactを別Endpointで返す。Mockの集約形をReal APIへ
		// そのまま期待せず、consumer境界でSessionDetailへ合成する。
		const [session, messages, executions, artifacts] = await Promise.all([
			this.get<AgentSession>(
				this.apiEndpoint(ENDPOINTS.session(scope.tenantId, scope.projectId, sessionId)),
			),
			this.get<Page<ChatMessage>>(
				`${this.apiEndpoint(ENDPOINTS.sessionChat(scope.tenantId, scope.projectId, sessionId))}${this.query({ limit: 200 })}`,
			),
			this.get<Page<WorkflowExecution>>(
				`${this.apiEndpoint(ENDPOINTS.executions(scope.tenantId, scope.projectId))}${this.query({ limit: 200, sessionId })}`,
			),
			this.get<Page<Artifact>>(
				`${this.apiEndpoint(ENDPOINTS.artifacts(scope.tenantId, scope.projectId))}${this.query({ limit: 200, sessionId })}`,
			),
		]);
		return {
			session,
			messages: messages.items,
			executions: executions.items,
			artifacts: artifacts.items,
		};
	}

	async sendChat(
		scope: Scope,
		sessionId: string,
		input: SendChatMessageRequest,
		idempotencyKey: string,
	) {
		const url = this.apiEndpoint(ENDPOINTS.sessionChat(scope.tenantId, scope.projectId, sessionId));
		const response = await this.raw<{
			userMessage: ChatMessage;
			agentMessage: ChatMessage;
			executionId: string | null;
			replayed?: boolean;
		}>(url, {
			method: "POST",
			body: JSON.stringify(input),
			headers: { [ContractHeaders.IDEMPOTENCY_KEY]: idempotencyKey },
		});
		return {
			userMessage: response.userMessage,
			agentMessage: response.agentMessage,
			executionId: response.executionId,
			replayed: response.replayed === true,
		};
	}

	async submitUserInput(
		scope: Scope,
		executionId: string,
		input: UserInputSubmission,
		idempotencyKey: string,
	): Promise<void> {
		const timeline = await this.getTimeline(scope, executionId);
		const wait = timeline.waitHandles.find(
			(candidate) => candidate.status === "OPEN" && candidate.waitType === "USER_INPUT",
		);
		if (!wait) {
			throw ApiError.conflict(
				"入力を受け付ける待機状態が見つかりません。進捗を確認してから再実行してください。",
				"USER_INPUT_WAIT_NOT_OPEN",
			);
		}
		const reissued = await this.post<{ resumeToken: string }>(
			this.apiEndpoint(ENDPOINTS.waitHandleReissueToken(scope.tenantId, scope.projectId, wait.id)),
			{ reason: "Guided Chatの入力を確定するため" },
		);
		await this.post(
			this.apiEndpoint(ENDPOINTS.waitHandleResolve(scope.tenantId, scope.projectId, wait.id)),
			{
				resumeToken: reissued.resumeToken,
				decision: "SUBMITTED",
				comment: "",
				payload: input,
			},
			{ [ContractHeaders.IDEMPOTENCY_KEY]: idempotencyKey },
		);
	}

	async getWorkOrderBoard(
		scope: Scope,
		options?: { progressStatuses?: readonly WorkOrderStatus[] },
	) {
		const ordersPage = await this.get<Page<WorkOrder>>(
			`${this.apiEndpoint(ENDPOINTS.workOrders(scope.tenantId, scope.projectId))}${this.query({ limit: 200 })}`,
		);
		const wanted = options?.progressStatuses ? new Set(options.progressStatuses) : null;
		const [progressItems, usage] = await Promise.all([
			Promise.all(
				ordersPage.items
					.filter((order) => !order.deletedAt && (!wanted || wanted.has(order.status)))
					.map((order) =>
						this.get<WorkOrderProgress>(
							this.apiEndpoint(
								ENDPOINTS.workOrderProgress(scope.tenantId, scope.projectId, order.id),
							),
						),
					),
			),
			this.get<LlmUsageSummary>(`${this.apiEndpoint(ENDPOINTS.llmUsageSummary())}?period=all`),
		]);
		const progress = Object.fromEntries(
			progressItems.map((item) => [
				item.orderId,
				progressView(
					item,
					usage,
					ordersPage.items.find((order) => order.id === item.orderId)?.status,
				),
			]),
		);
		const key = scopeKey(scope);
		const designSessionId = this.designSessions.get(key) ?? null;
		let designFrames = this.localFrames.get(key) ?? [];
		if (designSessionId) {
			const snapshot = await this.get<ConversationFramesSnapshot>(
				this.apiEndpoint(
					ENDPOINTS.designSessionFrames(scope.tenantId, scope.projectId, designSessionId),
				),
			);
			designFrames = mergeFrames(
				designFrames,
				snapshot.items.map((frame) => frameView(frame, designSessionId, null)),
			);
		}
		return { orders: ordersPage.items, progress, designSessionId, designFrames };
	}

	async getWorkOrderSessionFrames(
		scope: Scope,
		orderId: string,
		sessionId: string,
	): Promise<WorkOrderFrameView[]> {
		const snapshot = await this.get<ConversationFramesSnapshot>(
			this.apiEndpoint(
				ENDPOINTS.workOrderSessionFrames(scope.tenantId, scope.projectId, orderId, sessionId),
			),
		);
		return mergeFrames(
			this.localFrames.get(sessionId) ?? [],
			snapshot.items.map((frame) => frameView(frame, sessionId, orderId)),
		);
	}
	async getWorkOrderDetail(scope: Scope, orderId: string, options?: { frames?: "all" | "none" }) {
		const base = this.apiEndpoint(ENDPOINTS.workOrder(scope.tenantId, scope.projectId, orderId));
		const [order, questions, records, evidence, sessionPage, progress, usage] = await Promise.all([
			this.get<WorkOrder>(base),
			this.get<Page<WorkOrderQuestion>>(
				this.apiEndpoint(ENDPOINTS.workOrderQuestions(scope.tenantId, scope.projectId, orderId)),
			),
			this.get<Page<WorkOrderRecord>>(
				this.apiEndpoint(ENDPOINTS.workOrderRecords(scope.tenantId, scope.projectId, orderId)),
			),
			this.get<Page<WorkOrderEvidence>>(
				this.apiEndpoint(ENDPOINTS.workOrderEvidence(scope.tenantId, scope.projectId, orderId)),
			),
			this.get<{ items: WorkOrderSession[] }>(
				this.apiEndpoint(ENDPOINTS.workOrderSessions(scope.tenantId, scope.projectId, orderId)),
			),
			this.get<WorkOrderProgress>(
				this.apiEndpoint(ENDPOINTS.workOrderProgress(scope.tenantId, scope.projectId, orderId)),
			),
			this.get<LlmUsageSummary>(`${this.apiEndpoint(ENDPOINTS.llmUsageSummary())}?period=all`),
		]);
		const frameGroups = await Promise.all(
			sessionPage.items
				.filter((session) => session.framesAvailable && options?.frames !== "none")
				.map(async (session) => {
					const snapshot = await this.get<ConversationFramesSnapshot>(
						this.apiEndpoint(
							ENDPOINTS.workOrderSessionFrames(
								scope.tenantId,
								scope.projectId,
								orderId,
								session.id,
							),
						),
					);
					return mergeFrames(
						this.localFrames.get(session.id) ?? [],
						snapshot.items.map((frame) => frameView(frame, session.id, orderId)),
					);
				}),
		);
		return {
			order,
			questions: questions.items,
			records: records.items,
			evidence: evidence.items,
			sessions: sessionPage.items,
			frames: frameGroups.flat(),
			progress: progressView(progress, usage, order.status),
		};
	}
	transitionWorkOrder(
		scope: Scope,
		orderId: string,
		to: Exclude<WorkOrderStatus, "blocked">,
		reason?: string,
	): Promise<WorkOrder> {
		return this.post<WorkOrder>(
			this.apiEndpoint(ENDPOINTS.workOrderTransitions(scope.tenantId, scope.projectId, orderId)),
			{ to, ...(reason ? { reason } : {}) },
		);
	}
	deleteWorkOrder(scope: Scope, orderId: string): Promise<WorkOrder> {
		return this.delete<WorkOrder>(
			this.apiEndpoint(ENDPOINTS.workOrder(scope.tenantId, scope.projectId, orderId)),
		);
	}
	getWorkOrderProjectSettings(scope: Scope): Promise<WorkOrderProjectSettings> {
		return this.get<WorkOrderProjectSettings>(
			this.apiEndpoint(ENDPOINTS.workOrderProjectSettings(scope.tenantId, scope.projectId)),
		);
	}
	updateWorkOrderProjectSettings(
		scope: Scope,
		input: { runtimeParallelism: number; reason?: string },
	): Promise<WorkOrderProjectSettings> {
		return this.put<WorkOrderProjectSettings>(
			this.apiEndpoint(ENDPOINTS.workOrderProjectSettings(scope.tenantId, scope.projectId)),
			input,
		);
	}
	retryWorkOrder(
		scope: Scope,
		orderId: string,
		reason: string,
	): Promise<{ order: WorkOrder; jobId: string }> {
		return this.post<{ order: WorkOrder; jobId: string }>(
			this.apiEndpoint(ENDPOINTS.workOrderRetry(scope.tenantId, scope.projectId, orderId)),
			{ reason },
		);
	}
	restoreWorkOrder(scope: Scope, orderId: string): Promise<WorkOrder> {
		return this.post<WorkOrder>(
			this.apiEndpoint(ENDPOINTS.workOrderRestore(scope.tenantId, scope.projectId, orderId)),
			{},
		);
	}
	async answerWorkOrderQuestion(scope: Scope, orderId: string, questionId: string, answer: string) {
		await this.post<WorkOrderQuestion>(
			this.apiEndpoint(
				ENDPOINTS.workOrderQuestionAnswer(scope.tenantId, scope.projectId, orderId, questionId),
			),
			{ answerMarkdown: answer },
		);
		return this.getWorkOrderDetail(scope, orderId);
	}
	async sendBoardDesignMessage(scope: Scope, body: string) {
		const message = body.trim();
		if (!message) throw ApiError.validation("依頼内容を入力してください。");
		const key = scopeKey(scope);
		let designSessionId = this.designSessions.get(key);
		if (designSessionId) {
			await this.post<{ turnId: string }>(
				this.apiEndpoint(
					ENDPOINTS.designSessionInput(scope.tenantId, scope.projectId, designSessionId),
				),
				{ message },
			);
		} else {
			const session = await this.post<{ id: string }>(
				this.apiEndpoint(ENDPOINTS.designSessions(scope.tenantId, scope.projectId)),
				{ cli: "codex", message },
			);
			designSessionId = session.id;
			this.designSessions.set(key, designSessionId);
		}
		this.appendLocalFrame(key, designSessionId, null, message);
		const snapshot = await this.get<ConversationFramesSnapshot>(
			this.apiEndpoint(
				ENDPOINTS.designSessionFrames(scope.tenantId, scope.projectId, designSessionId),
			),
		);
		const frames = mergeFrames(
			this.localFrames.get(key) ?? [],
			snapshot.items.map((frame) => frameView(frame, designSessionId, null)),
		);
		return { frames, createdOrderIds: createdOrderIds(snapshot.items) };
	}
	async sendWorkOrderSessionMessage(
		scope: Scope,
		orderId: string,
		role: WorkOrderSessionRole,
		body: string,
	) {
		const message = body.trim();
		if (!message) throw ApiError.validation("メッセージを入力してください。");
		const page = await this.get<{ items: WorkOrderSession[] }>(
			this.apiEndpoint(ENDPOINTS.workOrderSessions(scope.tenantId, scope.projectId, orderId)),
		);
		const session = page.items
			.filter((candidate) => candidate.role === role && candidate.framesAvailable)
			.sort((left, right) => right.attempt - left.attempt)[0];
		if (!session) {
			throw ApiError.validation(`${roleLabel(role)}セッションには送信可能な frames がありません。`);
		}
		await this.post<{ turnId: string }>(
			this.apiEndpoint(
				ENDPOINTS.workOrderSessionInput(scope.tenantId, scope.projectId, orderId, session.id),
			),
			{ message },
		);
		this.appendLocalFrame(session.id, session.id, orderId, message);
		const snapshot = await this.get<ConversationFramesSnapshot>(
			this.apiEndpoint(
				ENDPOINTS.workOrderSessionFrames(scope.tenantId, scope.projectId, orderId, session.id),
			),
		);
		return mergeFrames(
			this.localFrames.get(session.id) ?? [],
			snapshot.items.map((frame) => frameView(frame, session.id, orderId)),
		);
	}

	subscribeDesignFrames(
		scope: Scope,
		designSessionId: string,
		options: WorkOrderStreamOptions<ConversationFrame>,
	): ProgressSubscription {
		return this.subscribeSse(
			this.apiEndpoint(
				ENDPOINTS.designSessionFramesStream(scope.tenantId, scope.projectId, designSessionId),
			),
			options,
			(frame) => ({
				sequence: frame.sequence,
				event: frame.event as ConversationFrame["event"],
				data: frame.data,
				createdAt: new Date().toISOString(),
			}),
		);
	}

	subscribeWorkOrderFrames(
		scope: Scope,
		orderId: string,
		sessionId: string,
		options: WorkOrderStreamOptions<ConversationFrame>,
	): ProgressSubscription {
		return this.subscribeSse(
			this.apiEndpoint(
				ENDPOINTS.workOrderSessionFramesStream(scope.tenantId, scope.projectId, orderId, sessionId),
			),
			options,
			(frame) => ({
				sequence: frame.sequence,
				event: frame.event as ConversationFrame["event"],
				data: frame.data,
				createdAt: new Date().toISOString(),
			}),
		);
	}

	subscribeWorkOrderProgress(
		scope: Scope,
		orderId: string,
		options: WorkOrderStreamOptions<WorkOrderProgress>,
	): ProgressSubscription {
		return this.subscribeSse(
			this.apiEndpoint(ENDPOINTS.workOrderProgressStream(scope.tenantId, scope.projectId, orderId)),
			options,
			(frame) => ({
				...(frame.data as Omit<WorkOrderProgress, "sequence" | "createdAt">),
				sequence: frame.sequence,
				createdAt: new Date().toISOString(),
			}),
		);
	}

	private appendLocalFrame(key: string, sessionId: string, orderId: string | null, body: string) {
		const current = this.localFrames.get(key) ?? [];
		this.localFrames.set(key, [
			...current,
			{
				id: `local-${sessionId}-${Date.now()}`,
				orderId,
				sessionId,
				sequence: -1,
				role: "user",
				body,
				createdAt: new Date().toISOString(),
			},
		]);
	}

	private subscribeSse<T>(
		url: string,
		options: WorkOrderStreamOptions<T>,
		map: (frame: SseFrame) => T,
	): ProgressSubscription {
		const controller = new AbortController();
		void this.readSse(
			url,
			options.lastSequence ?? -1,
			controller.signal,
			(frame) => {
				options.onEvent(map(frame));
			},
			options.onGap,
		).catch((error: unknown) => {
			if (!controller.signal.aborted) options.onError(error);
		});
		return { close: () => controller.abort() };
	}

	private async readSse(
		url: string,
		lastSequence: number,
		signal: AbortSignal,
		onFrame: (frame: SseFrame) => void,
		onGap: (expected: number, received: number) => void,
	): Promise<void> {
		let response: Response;
		try {
			response = await this.fetchImpl(url, {
				headers: {
					accept: "text/event-stream",
					[ContractHeaders.CONTRACT_VERSION]: manifest.contractVersion,
					...(this.auth ? { authorization: `Bearer ${this.auth.accessToken}` } : {}),
					...(lastSequence >= 0 ? { "last-event-id": String(lastSequence) } : {}),
				},
				signal,
			});
		} catch (cause) {
			throw new TransportError(`Zerovia SSEへ接続できませんでした: ${url}`, url, cause);
		}
		if (!response.ok || !response.body) {
			throw new ApiError({
				code: "INTERNAL",
				message: `SSE HTTP ${response.status}`,
				status: response.status,
			});
		}
		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";
		let expected = lastSequence + 1;
		while (!signal.aborted) {
			const { value, done } = await reader.read();
			buffer += decoder.decode(value, { stream: !done });
			const blocks = buffer.split(/\r?\n\r?\n/);
			buffer = blocks.pop() ?? "";
			for (const block of blocks) {
				const frame = parseSseFrame(block);
				if (!frame) continue;
				if (frame.sequence !== expected) onGap(expected, frame.sequence);
				expected = frame.sequence + 1;
				onFrame(frame);
			}
			if (done) break;
		}
	}

	startExecution(scope: Scope, input: StartExecutionRequest): Promise<WorkflowExecution> {
		return this.post<WorkflowExecution>(
			this.apiEndpoint(ENDPOINTS.executions(scope.tenantId, scope.projectId)),
			StartExecutionRequestSchema.parse(input),
		);
	}

	listExecutions(scope: Scope, query?: ListQuery): Promise<Page<WorkflowExecution>> {
		return this.get<Page<WorkflowExecution>>(
			`${this.apiEndpoint(ENDPOINTS.executions(scope.tenantId, scope.projectId))}${this.query(query)}`,
		);
	}

	getTimeline(scope: Scope, executionId: string): Promise<ExecutionTimeline> {
		return this.get<ExecutionTimeline>(
			this.apiEndpoint(ENDPOINTS.executionTimeline(scope.tenantId, scope.projectId, executionId)),
		);
	}

	sendWorkflowCommand(
		scope: Scope,
		executionId: string,
		command: WorkflowCommand,
		options: CommandOptions,
	): Promise<WorkflowExecution> {
		return this.post<WorkflowExecution>(
			this.apiEndpoint(
				ENDPOINTS.executionCommand(scope.tenantId, scope.projectId, executionId, command),
			),
			options,
			{
				[ContractHeaders.IDEMPOTENCY_KEY]: options.idempotencyKey,
				[ContractHeaders.REASON]: encodeURIComponent(options.reason),
			},
		);
	}

	subscribeProgress(
		scope: Scope,
		executionId: string,
		options: SubscribeOptions,
	): ProgressSubscription {
		const url = this.apiEndpoint(
			ENDPOINTS.executionEvents(scope.tenantId, scope.projectId, executionId),
		);
		if (typeof EventSource === "undefined") {
			queueMicrotask(() =>
				options.onError(new TransportError("この環境ではSSEを利用できません。", url)),
			);
			return { close() {} };
		}
		const source = new EventSource(url, { withCredentials: false });
		let expected = (options.lastSequence ?? -1) + 1;

		source.onmessage = (message) => {
			const parsed = ProgressEventSchema.safeParse(safeJson(message.data));
			if (!parsed.success) return;
			const event: ProgressEvent = parsed.data;
			if (event.sequence !== expected) options.onGap(expected, event.sequence);
			expected = event.sequence + 1;
			options.onEvent(event);
		};
		source.onerror = (error) => options.onError(error);

		return {
			close() {
				source.close();
			},
		};
	}

	listArtifacts(scope: Scope, query?: ListQuery & { sessionId?: string }): Promise<Page<Artifact>> {
		return this.get<Page<Artifact>>(
			`${this.apiEndpoint(ENDPOINTS.artifacts(scope.tenantId, scope.projectId))}${this.query(query)}`,
		);
	}

	createArtifact(scope: Scope, input: CreateArtifactRequest): Promise<Artifact> {
		return this.post<Artifact>(
			this.apiEndpoint(ENDPOINTS.artifacts(scope.tenantId, scope.projectId)),
			input,
		);
	}

	getArtifact(scope: Scope, artifactId: string): Promise<Artifact> {
		return this.get<Artifact>(
			this.apiEndpoint(ENDPOINTS.artifact(scope.tenantId, scope.projectId, artifactId)),
		);
	}

	async getArtifactContent(scope: Scope, artifactId: string) {
		const [artifact, response] = await Promise.all([
			this.getArtifact(scope, artifactId),
			this.get<ArtifactContent>(
				this.apiEndpoint(ENDPOINTS.artifactContent(scope.tenantId, scope.projectId, artifactId)),
			),
		]);
		return { artifact, content: response.content, checksumVerified: response.checksumVerified };
	}

	async createDownloadTicket(scope: Scope, artifactId: string): Promise<DownloadTicket> {
		const artifact = await this.getArtifact(scope, artifactId);
		return {
			url: this.apiEndpoint(
				ENDPOINTS.artifactDownload(scope.tenantId, scope.projectId, artifactId),
			),
			artifactId,
			expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
			checksum: artifact.checksum,
		};
	}

	listCostEvents(scope: Scope, query?: ListQuery): Promise<Page<CostEventView>> {
		return this.get<Page<CostEventView>>(
			`${this.apiEndpoint(ENDPOINTS.costs(scope.tenantId, scope.projectId))}${this.query(query)}`,
		);
	}

	listAuditEvents(scope: Scope, query?: ListQuery): Promise<Page<AuditEventView>> {
		return this.get<Page<AuditEventView>>(
			`${this.apiEndpoint(ENDPOINTS.audit(scope.tenantId, scope.projectId))}${this.query(query)}`,
		);
	}

	// -- delegated to local fallback (Zerovia未実装) ----------------------------

	listOrganizationProjects(
		organizationId: string,
		options?: { includeArchived?: boolean },
	): Promise<ProjectListItemView[]> {
		const suffix = options?.includeArchived ? "?includeArchived=true" : "";
		return this.get(`${this.apiEndpoint(ENDPOINTS.organizationProjects(organizationId))}${suffix}`);
	}

	listWorkspaceTypes(organizationId: string): Promise<WorkspaceTypeList> {
		return this.get(this.apiEndpoint(ENDPOINTS.organizationWorkspaceTypes(organizationId)));
	}

	createProject(organizationId: string, input: CreateProjectRequest): Promise<Project> {
		return this.post(this.apiEndpoint(ENDPOINTS.organizationProjects(organizationId)), input);
	}

	archiveProject(organizationId: string, projectId: string): Promise<Project> {
		return this.delete(this.apiEndpoint(ENDPOINTS.organizationProject(organizationId, projectId)));
	}

	restoreProject(organizationId: string, projectId: string): Promise<Project> {
		return this.post(
			this.apiEndpoint(ENDPOINTS.organizationProjectRestore(organizationId, projectId)),
			{},
		);
	}

	listOrganizationRepositories(organizationId: string): Promise<GitRepository[]> {
		return this.get(this.apiEndpoint(ENDPOINTS.organizationRepositories(organizationId)));
	}

	listProjectGitRepositories(
		organizationId: string,
		projectId: string,
	): Promise<GitRepository[]> {
		return this.get(this.apiEndpoint(ENDPOINTS.projectGitRepos(organizationId, projectId)));
	}

	ensureProjectGitRepository(
		organizationId: string,
		projectId: string,
		input: Partial<EnsureGitRepositoryRequest> = {},
	): Promise<EnsureGitRepositoryResponse> {
		return this.post(
			this.apiEndpoint(ENDPOINTS.projectGitRepos(organizationId, projectId)),
			input,
		);
	}

	listProjectRepositories(
		organizationId: string,
		projectId: string,
	): Promise<ProjectRepositoryConnection[]> {
		return this.get(this.apiEndpoint(ENDPOINTS.projectRepositories(organizationId, projectId)));
	}

	connectProjectRepository(
		organizationId: string,
		projectId: string,
		repositoryId: string,
	): Promise<ProjectRepositoryConnection> {
		return this.post(this.apiEndpoint(ENDPOINTS.projectRepositories(organizationId, projectId)), {
			repositoryId,
		});
	}

	async disconnectProjectRepository(
		organizationId: string,
		projectId: string,
		repositoryId: string,
	): Promise<void> {
		await this.delete(
			this.apiEndpoint(ENDPOINTS.projectRepository(organizationId, projectId, repositoryId)),
		);
	}

	async listPersonalCredentials(scope: Scope): Promise<PersonalCredential[]> {
		const [profilePayload, providers] = await Promise.all([
			this.get<unknown>(
				this.apiEndpoint(ENDPOINTS.authenticationProfiles(scope.tenantId, scope.projectId)),
			),
			this.listProviders(scope),
		]);
		const providerLabels = new Map(
			providers.items.map((provider) => [provider.id, provider.displayName]),
		);
		return AuthenticationProfilePageSchema.parse(profilePayload)
			.items.filter(
				(profile) =>
					profile.ownerType === "USER" &&
					profile.ownerId === this.auth?.user.id &&
					profile.status !== "DISABLED",
			)
			.map((profile) => personalCredential(profile, providerLabels.get(profile.providerId)));
	}
	async createPersonalCredential(
		scope: Scope,
		input: Parameters<ZeroviaClient["createPersonalCredential"]>[1],
	) {
		if (!/^(?:env|vault):[A-Za-z0-9_\-./]+$/.test(input.secretRef)) {
			throw ApiError.validation(
				"Secret本文は保存できません。env: / vault: 形式の参照を入力してください。",
				"SECRET_LITERAL_REJECTED",
			);
		}
		if (!this.auth) throw new ApiError({ code: "UNAUTHORIZED", message: "Login required" });
		const created = AuthenticationProfileSchema.parse(
			await this.post<unknown>(
				this.apiEndpoint(ENDPOINTS.authenticationProfiles(scope.tenantId, scope.projectId)),
				{
					providerId: input.providerId,
					authMethod: input.authMethod === "API_KEY" ? "API_KEY" : "CLI_SESSION",
					accountType:
						input.authMethod === "API_KEY" ? "INDIVIDUAL_API" : "INDIVIDUAL_SUBSCRIPTION",
					ownerType: "USER",
					ownerId: this.auth.user.id,
					displayName: input.displayName,
					credentialClass: input.authMethod === "API_KEY" ? "USER_API" : "USER_SUBSCRIPTION",
					secretRef: input.secretRef,
				},
			),
		);
		const providers = await this.listProviders(scope);
		return personalCredential(
			created,
			providers.items.find((provider) => provider.id === created.providerId)?.displayName,
		);
	}
	async testPersonalCredential(scope: Scope, credentialId: string) {
		const result = ConnectionTestResultSchema.parse(
			await this.post<unknown>(
				this.apiEndpoint(
					ENDPOINTS.authenticationProfileTest(scope.tenantId, scope.projectId, credentialId),
				),
				{},
			),
		);
		return {
			result: result.ok ? ("SUCCESS" as const) : ("FAILED" as const),
			reason: result.reason,
			verifiedAt: result.testedAt,
		};
	}
	async revokePersonalCredential(scope: Scope, credentialId: string) {
		await this.post<unknown>(
			this.apiEndpoint(
				ENDPOINTS.authenticationProfileStatus(scope.tenantId, scope.projectId, credentialId),
			),
			{ status: "DISABLED" },
		);
	}
	async getSessionAuthRoutes(scope: Scope, _sessionId: string): Promise<AuthRouteView[]> {
		const query = "?limit=200";
		const [providerPayload, poolPayload, assignmentPayload] = await Promise.all([
			this.get<unknown>(`${this.apiEndpoint(ENDPOINTS.platformProviders())}${query}`),
			this.get<unknown>(`${this.apiEndpoint(ENDPOINTS.systemAuthPools())}${query}`),
			this.get<unknown>(`${this.apiEndpoint(ENDPOINTS.systemAuthAssignments())}${query}`),
		]);
		const providers = ProviderPageSchema.parse(providerPayload)
			.items.filter((provider) => provider.status === "ACTIVE")
			.sort((left, right) => left.priority - right.priority || left.key.localeCompare(right.key));
		const pools = SystemAuthPoolPageSchema.parse(poolPayload).items.filter(
			(pool) => pool.status === "ACTIVE",
		);
		const assignments = SystemAuthAssignmentPageSchema.parse(assignmentPayload).items;
		const now = Date.now();
		const visiblePools = pools.filter((pool) => {
			const matching = assignments.filter(
				(assignment) =>
					assignment.targetType === "POOL" &&
					assignment.targetId === pool.id &&
					assignmentApplies(assignment, scope, this.auth?.user.id, this.auth?.membership.role, now),
			);
			return (
				!matching.some((assignment) => assignment.effect === "DENY") &&
				matching.some((assignment) => assignment.effect === "ALLOW")
			);
		});

		const routableProviders = providers.filter((provider) =>
			visiblePools.some((pool) => pool.providerId === provider.id),
		);
		return routableProviders.flatMap<AuthRouteView>((provider, providerIndex) => {
			const providerPools = visiblePools.filter((pool) => pool.providerId === provider.id);
			if (providerPools.length === 0) return [];
			return [
				{
					routeKey: provider.key,
					providerLabel: provider.displayName,
					taskTypes: [...new Set(providerPools.flatMap((pool) => pool.purposeScope))],
					candidates: providerPools.map((pool) => ({
						label: pool.displayAlias,
						credentialClass:
							pool.authMethod === "CLI_SESSION" ? "SYSTEM_SUBSCRIPTION" : "SYSTEM_API",
						poolAlias: pool.displayAlias,
						state: providerIndex === 0 ? "PRIMARY" : "FALLBACK",
						unavailableReason: null,
					})),
				},
			];
		});
	}
	async listProjects(scope: Scope) {
		const projects = await this.listOrganizationProjects(scope.tenantId);
		return projects.map((project) => ({
			id: project.id,
			name: project.name,
			description: "",
		}));
	}
	async listApprovals(scope: Scope): Promise<ApprovalRequest[]> {
		const executions = await this.listExecutions(scope, { limit: 200 });
		const timelines = await Promise.all(
			executions.items
				.filter(
					(execution) => execution.status === "WAITING" && execution.waitReason === "USER_APPROVAL",
				)
				.map((execution) => this.getTimeline(scope, execution.id)),
		);
		const waits = timelines.flatMap((timeline) =>
			timeline.waitHandles.filter(
				(wait) => wait.status === "OPEN" && wait.waitType === "USER_APPROVAL",
			),
		);
		if (waits.length === 0) return [];
		const artifactPage = await this.listArtifacts(scope, { limit: 200 });
		return waits.map((wait) => {
			const timeline = timelines.find((candidate) => candidate.execution.id === wait.executionId);
			if (!timeline) throw ApiError.notFound("Approval execution timeline not found");
			return this.toApproval(wait, timeline, artifactPage.items);
		});
	}
	async getApproval(scope: Scope, approvalId: string): Promise<ApprovalRequest> {
		const approval = (await this.listApprovals(scope)).find((item) => item.id === approvalId);
		if (!approval) throw ApiError.notFound("Approval not found");
		return approval;
	}
	async decideApproval(
		scope: Scope,
		approvalId: string,
		input: {
			decision: ApprovalDecision;
			selectedArtifactId?: string;
			selectedArtifactVersion?: number;
			siteId: ApprovalRequest["siteId"];
			instructions: string;
			idempotencyKey: string;
		},
	): Promise<ApprovalRequest> {
		if (input.decision === "APPROVED" && !input.selectedArtifactId) {
			throw ApiError.validation("承認する案を選んでください。");
		}
		if (input.decision === "CHANGES_REQUESTED" && !input.instructions.trim()) {
			throw ApiError.validation("修正指示を入力してください。");
		}
		const approval = await this.getApproval(scope, approvalId);
		const payload = DesignApprovalPayloadSchema.parse({
			selectedArtifactId: input.selectedArtifactId,
			selectedArtifactVersion: input.selectedArtifactVersion,
			siteId: input.siteId,
			instructions: input.instructions,
		});
		const reissued = await this.post<{ resumeToken: string }>(
			this.apiEndpoint(
				ENDPOINTS.waitHandleReissueToken(scope.tenantId, scope.projectId, approvalId),
			),
			{ reason: "承認画面の決定を確定するため" },
		);
		await this.post(
			this.apiEndpoint(ENDPOINTS.waitHandleResolve(scope.tenantId, scope.projectId, approvalId)),
			{
				resumeToken: reissued.resumeToken,
				decision: input.decision,
				comment: input.instructions,
				payload,
			},
			{ [ContractHeaders.IDEMPOTENCY_KEY]: input.idempotencyKey },
		);
		return {
			...approval,
			status: input.decision === "APPROVED" ? "PASSED" : "FAILED",
			decision: input.decision,
			decidedBy: this.auth?.user.id ?? null,
			decidedAt: new Date().toISOString(),
		};
	}

	private toApproval(
		wait: WaitHandle,
		timeline: ExecutionTimeline,
		artifacts: Artifact[],
	): ApprovalRequest {
		const indexArtifactId = wait.correlationKey?.startsWith("lp-design:")
			? (wait.correlationKey.split(":").at(-1) ?? null)
			: null;
		const proposalArtifacts = artifacts.filter(
			(artifact) =>
				artifact.generatedByExecutionId === wait.executionId && artifact.type === "lp.proposal",
		);
		const artifactIds = proposalArtifacts.map((artifact) => artifact.id);
		if (indexArtifactId && !artifactIds.includes(indexArtifactId))
			artifactIds.push(indexArtifactId);
		const proposalSite = proposalArtifacts
			.map(
				(artifact) => artifact.name.match(/^proposal-(default|lp|newbiz|creator)-\d+\.json$/)?.[1],
			)
			.find((site): site is ApprovalRequest["siteId"] => site !== undefined);
		return {
			id: wait.id,
			tenantId: timeline.execution.tenantId,
			projectId: timeline.execution.projectId,
			sessionId: timeline.execution.sessionId,
			executionId: wait.executionId,
			nodeExecutionId: wait.nodeExecutionId,
			gateType: "APPROVAL",
			title: "LP構成案を選ぶ",
			summary: "3つの構成案を比較し、採用する案を選んでください。",
			evidence: [{ label: "構成案", value: `${proposalArtifacts.length}案` }],
			artifactIds,
			indexArtifactId,
			siteId: proposalSite ?? "default",
			status: "PENDING",
			decision: null,
			decidedBy: null,
			decidedAt: null,
			expiresAt: wait.expiresAt,
			changeRequestRoute: "design",
		};
	}
	/**
	 * Zerovia側にVersion一覧APIが無い。
	 * Mockへ委譲すると実Artifact IDが見つからず404になるため、
	 * 同一Session内の同名Artifactを実データから導出する。
	 */
	async listArtifactVersions(scope: Scope, artifactId: string): Promise<Artifact[]> {
		const artifact = await this.getArtifact(scope, artifactId);
		const page = await this.listArtifacts(scope, {
			limit: 200,
			sessionId: artifact.generatedInSessionId ?? undefined,
		});
		const sameName = page.items.filter((item) => item.name === artifact.name);
		const versions = sameName.length > 0 ? sameName : [artifact];
		return versions.sort((a, b) => b.version - a.version);
	}
	transferArtifactOwnership(
		scope: Scope,
		artifactId: string,
		input: { toOwnerUserId: string; reason: string },
	): Promise<OwnershipTransferRecord> {
		return this.fallback.transferArtifactOwnership(scope, artifactId, input);
	}
	/** Zerovia側に移管履歴APIが無い。存在しない履歴を捏造せず空で返す。 */
	async listOwnershipTransfers(
		_scope: Scope,
		_artifactId: string,
	): Promise<OwnershipTransferRecord[]> {
		return [];
	}
	/** Workspace APIは未実装。確定済みArtifactを成果物フォルダとして提示する。 */
	async listWorkspaceEntries(scope: Scope): Promise<WorkspaceEntry[]> {
		const page = await this.listArtifacts(scope, { limit: 200 });
		return page.items.map((artifact) => ({
			id: `ws-${artifact.id}`,
			tenantId: artifact.tenantId,
			projectId: artifact.projectId,
			path: `/deliverables/${artifact.name}`,
			kind: "FILE" as const,
			mimeType: artifact.mimeType,
			size: artifact.sizeBytes,
			artifactId: artifact.id,
			updatedAt: artifact.createdAt,
		}));
	}
	/** Storage一覧APIは未実装。実ArtifactのstorageRef/checksumから保存先を組み立てる。 */
	async listStorageObjects(scope: Scope): Promise<StorageObject[]> {
		const page = await this.listArtifacts(scope, { limit: 200 });
		return page.items.map((artifact) => ({
			id: `storage-${artifact.id}`,
			tenantId: artifact.tenantId,
			projectId: artifact.projectId,
			artifactId: artifact.id,
			objectRef: artifact.storageRef,
			mimeType: artifact.mimeType,
			size: artifact.sizeBytes,
			checksum: artifact.checksum,
			storageClass: "STANDARD" as const,
			retentionUntil: null,
			createdAt: artifact.createdAt,
		}));
	}
	getUsageSummary(scope: Scope): Promise<UsageSummary> {
		return this.fallback.getUsageSummary(scope);
	}
	listNotifications(scope: Scope): Promise<NotificationItem[]> {
		return this.fallback.listNotifications(scope);
	}
	markNotificationRead(scope: Scope, notificationId: string) {
		return this.fallback.markNotificationRead(scope, notificationId);
	}
	listMembers(scope: Scope): Promise<TenantMember[]> {
		return this.fallback.listMembers(scope);
	}
	updateMemberRole(scope: Scope, memberId: string, role: TenantMember["role"]) {
		return this.fallback.updateMemberRole(scope, memberId, role);
	}
	listInvites(scope: Scope): Promise<TenantInvite[]> {
		return this.fallback.listInvites(scope);
	}
	createInvite(scope: Scope, input: { email: string; role: TenantMember["role"] }) {
		return this.fallback.createInvite(scope, input);
	}
	revokeInvite(scope: Scope, inviteId: string) {
		return this.fallback.revokeInvite(scope, inviteId);
	}
	listRoleDefinitions(scope: Scope): Promise<RoleDefinition[]> {
		return this.fallback.listRoleDefinitions(scope);
	}
	listCatalogEntitlements(scope: Scope): Promise<TenantCatalogEntitlement[]> {
		return this.fallback.listCatalogEntitlements(scope);
	}
	setCatalogEntitlement(scope: Scope, ucsId: string, enabled: boolean) {
		return this.fallback.setCatalogEntitlement(scope, ucsId, enabled);
	}
	listPoolAssignments(scope: Scope): Promise<PoolAssignment[]> {
		return this.fallback.listPoolAssignments(scope);
	}
	setPoolAssignment(
		scope: Scope,
		input: { poolId: string; subjectId: string; effect: "ALLOW" | "DENY" },
	) {
		return this.fallback.setPoolAssignment(scope, input);
	}
	getByoPolicy(scope: Scope): Promise<ByoPolicy> {
		return this.fallback.getByoPolicy(scope);
	}
	setByoPolicy(scope: Scope, input: { byokAllowed: boolean; byosAllowed: boolean }) {
		return this.fallback.setByoPolicy(scope, input);
	}
	listProviderUsageRules(scope: Scope): Promise<ProviderUsageRule[]> {
		return this.fallback.listProviderUsageRules(scope);
	}
	listBudgets(scope: Scope): Promise<Budget[]> {
		return this.fallback.listBudgets(scope);
	}
	updateBudget(scope: Scope, budgetId: string, limitAmount: number) {
		return this.fallback.updateBudget(scope, budgetId, limitAmount);
	}
	listIntegrations(scope: Scope): Promise<Integration[]> {
		return this.fallback.listIntegrations(scope);
	}
	getTenantSettings(scope: Scope): Promise<TenantSettings> {
		return this.fallback.getTenantSettings(scope);
	}
	updateTenantSettings(scope: Scope, input: Partial<TenantSettings>) {
		return this.fallback.updateTenantSettings(scope, input);
	}
	listTenants(): Promise<TenantSummary[]> {
		return this.fallback.listTenants();
	}
	getTenantSummary(tenantId: string): Promise<TenantSummary> {
		return this.fallback.getTenantSummary(tenantId);
	}
	listAllUsers(): Promise<TenantMember[]> {
		return this.fallback.listAllUsers();
	}
	listPublications(): Promise<CatalogPublication[]> {
		return this.fallback.listPublications();
	}
	setPublicationStatus(ucsId: string, status: CatalogPublication["status"]) {
		return this.fallback.setPublicationStatus(ucsId, status);
	}
	listPlans(): Promise<Plan[]> {
		return this.fallback.listPlans();
	}
	updatePlan(planId: string, input: Partial<Plan>) {
		return this.fallback.updatePlan(planId, input);
	}
	listNotices(): Promise<Notice[]> {
		return this.fallback.listNotices();
	}
	upsertNotice(input: Partial<Notice> & { title: string; body: string }) {
		return this.fallback.upsertNotice(input);
	}
	listContactMessages(): Promise<ContactMessage[]> {
		return this.fallback.listContactMessages();
	}
	updateContactStatus(id: string, status: ContactMessage["status"]) {
		return this.fallback.updateContactStatus(id, status);
	}
	getProductAnalytics() {
		return this.fallback.getProductAnalytics();
	}
	getProductSettings(): Promise<ProductSettings> {
		return this.fallback.getProductSettings();
	}
	updateProductSettings(input: Partial<ProductSettings>) {
		return this.fallback.updateProductSettings(input);
	}
}

const ZEROVIA_OPERATIONS = new Set<keyof ZeroviaClient>([
	"login",
	"signup",
	"me",
	"logout",
	"getUserAttributes",
	"upsertUserAttributes",
	"listOrganizationProjects",
	"listWorkspaceTypes",
	"createProject",
	"listProjectGitRepositories",
	"ensureProjectGitRepository",
	"getCatalog",
	"getDashboard",
	"listProviders",
	"listAuthProfiles",
	"listPersonalCredentials",
	"createPersonalCredential",
	"testPersonalCredential",
	"revokePersonalCredential",
	"listSessions",
	"createSession",
	"getSession",
	"sendChat",
	"submitUserInput",
	"getWorkOrderBoard",
	"getWorkOrderDetail",
	"getWorkOrderSessionFrames",
	"transitionWorkOrder",
	"deleteWorkOrder",
	"getWorkOrderProjectSettings",
	"updateWorkOrderProjectSettings",
	"retryWorkOrder",
	"restoreWorkOrder",
	"answerWorkOrderQuestion",
	"sendBoardDesignMessage",
	"sendWorkOrderSessionMessage",
	"subscribeDesignFrames",
	"subscribeWorkOrderFrames",
	"subscribeWorkOrderProgress",
	"listExecutions",
	"getTimeline",
	"sendWorkflowCommand",
	"subscribeProgress",
	"listApprovals",
	"getApproval",
	"decideApproval",
	"listArtifacts",
	"createArtifact",
	"getArtifact",
	"getArtifactContent",
	"createDownloadTicket",
	"listCostEvents",
	"listAuditEvents",
	"getSessionAuthRoutes",
	"verifyContract",
]);

function personalCredential(
	profile: AuthenticationProfile,
	providerLabel = profile.providerId,
): PersonalCredential {
	return {
		id: profile.id,
		userId: profile.ownerId,
		providerId: profile.providerId,
		providerLabel,
		displayName: profile.displayName,
		authMethod: profile.authMethod === "API_KEY" ? "API_KEY" : "SUBSCRIPTION_LOGIN",
		secretRef: profile.secretRef,
		maskedHint: `••••${profile.secretRef.slice(-4)}`,
		status:
			profile.status === "ACTIVE"
				? "ACTIVE"
				: profile.status === "EXPIRED"
					? "REAUTH_REQUIRED"
					: profile.status === "DISABLED"
						? "REVOKED"
						: "ERROR",
		lastVerifiedAt: profile.verifiedAt,
		lastTestResult: null,
		lastTestReason: profile.status === "EXPIRED" ? "AUTH_PROFILE_EXPIRED" : null,
		createdAt: profile.createdAt,
	};
}

function assignmentApplies(
	assignment: {
		subjectType: "USER" | "GROUP" | "ROLE" | "TENANT" | "PROJECT" | "ANY_TENANT";
		subjectId: string;
		validFrom: string | null;
		validUntil: string | null;
	},
	scope: Scope,
	userId: string | undefined,
	role: string | undefined,
	now: number,
): boolean {
	if (assignment.validFrom && Date.parse(assignment.validFrom) > now) return false;
	if (assignment.validUntil && Date.parse(assignment.validUntil) <= now) return false;
	switch (assignment.subjectType) {
		case "ANY_TENANT":
			return assignment.subjectId === "*";
		case "TENANT":
			return assignment.subjectId === scope.tenantId;
		case "PROJECT":
			return assignment.subjectId === scope.projectId;
		case "USER":
			return assignment.subjectId === userId;
		case "ROLE":
			return assignment.subjectId === role;
		case "GROUP":
			return false;
	}
}

function readStoredAuth(): AuthSession | null {
	try {
		const raw = localStorage?.getItem(SESSION_KEY);
		if (!raw) return null;
		const value: unknown = JSON.parse(raw);
		const bootstrap = SessionBootstrapSchema.safeParse(value);
		const auth = bootstrap.success ? bootstrap : AuthSessionSchema.safeParse(value);
		if (!auth.success || refreshExpired(auth.data)) {
			localStorage?.removeItem(SESSION_KEY);
			return null;
		}
		return auth.data;
	} catch {
		try {
			localStorage?.removeItem(SESSION_KEY);
		} catch {
			/* storage unavailable */
		}
		return null;
	}
}

function refreshExpired(session: AuthSession): boolean {
	const expiresAt = Date.parse(session.refreshExpiresAt);
	return Number.isNaN(expiresAt) || expiresAt <= Date.now();
}

function accessNeedsRefresh(session: AuthSession): boolean {
	const expiresAt = Date.parse(session.expiresAt);
	return Number.isNaN(expiresAt) || expiresAt - Date.now() <= ACCESS_REFRESH_MARGIN_MS;
}

function safeJson(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}

function parseUserAttributeMap(userId: string, payload: unknown): UserAttributeMap {
	const body = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {};
	const rawItems = Array.isArray(body.items) ? body.items : [];
	return {
		userId: typeof body.userId === "string" ? body.userId : userId,
		items: rawItems.flatMap((item) => {
			if (!item || typeof item !== "object") return [];
			const row = item as Record<string, unknown>;
			if (typeof row.key !== "string" || typeof row.value !== "string") return [];
			return [
				{
					key: row.key,
					value: row.value,
					updatedAt: typeof row.updatedAt === "string" ? row.updatedAt : "",
					updatedBy: typeof row.updatedBy === "string" ? row.updatedBy : "",
				},
			];
		}),
	};
}

type SseFrame = { sequence: number; event: string; data: unknown };

function parseSseFrame(block: string): SseFrame | null {
	let id: number | null = null;
	let event = "message";
	const data: string[] = [];
	for (const line of block.split(/\r?\n/)) {
		if (line.startsWith("id:")) id = Number(line.slice(3).trim());
		else if (line.startsWith("event:")) event = line.slice(6).trim();
		else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
	}
	if (id === null || !Number.isInteger(id)) return null;
	return { sequence: id, event, data: safeJson(data.join("\n")) };
}

function scopeKey(scope: Scope): string {
	return `${scope.tenantId}:${scope.projectId}`;
}

function frameView(
	frame: ConversationFrame,
	sessionId: string,
	orderId: string | null,
): WorkOrderFrameView {
	return {
		id: `${sessionId}:${frame.sequence}`,
		orderId,
		sessionId,
		sequence: frame.sequence,
		role: frame.event === "stdout" ? "assistant" : "system",
		body: frameBody(frame),
		createdAt: frame.createdAt,
	};
}

function frameBody(frame: ConversationFrame): string {
	if (frame.event === "orders_created") {
		return `下書きカードを起票しました: ${createdOrderIds([frame]).join(", ")}`;
	}
	if (frame.event === "exec") return "担当エージェントが処理を開始しました。";
	if (frame.event === "exit") return "担当エージェントの処理が終了しました。";
	if (frame.event === "truncated") return "長い出力の一部を省略しました。";
	if (typeof frame.data === "string") return frame.data;
	if (frame.data && typeof frame.data === "object") {
		const record = frame.data as Record<string, unknown>;
		const message = typeof record.message === "string" ? record.message : "";
		const detail = typeof record.detail === "string" ? record.detail : "";
		if (message && detail) return `${message}: ${detail}`;
		for (const key of ["data", "message", "detail", "error"]) {
			if (typeof record[key] === "string" && record[key]) return record[key] as string;
		}
	}
	return frame.event === "stderr" || frame.event === "error"
		? "処理中にエラーが報告されました。"
		: "工程イベントを受信しました。";
}

function createdOrderIds(frames: ConversationFrame[]): string[] {
	return frames.flatMap((frame) => {
		if (frame.event !== "orders_created" || !frame.data || typeof frame.data !== "object")
			return [];
		const ids = (frame.data as { orderIds?: unknown }).orderIds;
		return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === "string") : [];
	});
}

function mergeFrames(
	local: WorkOrderFrameView[],
	remote: WorkOrderFrameView[],
): WorkOrderFrameView[] {
	return [...local, ...remote].sort((left, right) => {
		const byTime = left.createdAt.localeCompare(right.createdAt);
		return byTime === 0 ? left.sequence - right.sequence : byTime;
	});
}

function progressView(
	progress: WorkOrderProgress,
	usage: LlmUsageSummary,
	orderStatus?: WorkOrderStatus,
): WorkOrderProgressView {
	const llmActiveSeconds = usage.assignments
		.filter((assignment) => assignment.workOrderId === progress.orderId)
		.reduce((sum, assignment) => sum + assignment.activeSeconds, 0);
	return {
		sequence: progress.sequence,
		phase: progress.phase,
		label:
			orderStatus === "accepted"
				? "受け入れ完了"
				: orderStatus === "rejected"
					? "差し戻し"
					: progressLabel(progress.phase),
		startedAt: progress.startedAt ?? progress.createdAt,
		retryCount: progress.retryCount,
		retryReason: progress.retryReason,
		nextRetryAt: progress.nextRetryAt,
		failureClass: progress.failureClass,
		warning: progress.warning,
		wallClock: progress.wallClock,
		journey: progress.journey,
		live:
			Boolean(orderStatus && ["issued", "implemented", "verified"].includes(orderStatus)) &&
			!["succeeded", "failed", "canceled"].includes(progress.phase),
		llmActiveSeconds,
	};
}

function progressLabel(phase: WorkOrderProgress["phase"]): string {
	return {
		queued: "実行待ち",
		preparing: "実行準備中",
		executing: "実装中",
		verifying: "検証中",
		finalizing: "最終処理中",
		integrating: "canonical 統合中",
		retry_wait: "再試行待ち",
		succeeded: "工程完了",
		failed: "工程失敗",
		canceled: "工程中止",
	}[phase];
}

function roleLabel(role: WorkOrderSessionRole): string {
	return { design: "設計", implement: "実装", accept: "受け入れ", journey: "ジャーニー" }[role];
}
